import { Component } from '@theme/component';

class CopyToClipboard extends Component {
  constructor() {
    super();
    this.copyButton = null;
    this.resetTimeoutId = null;
    this.handleCopy = this.handleCopy.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.copyButton = this.querySelector('[data-copy-button]');
    this.valueElement = this.querySelector('.copy-to-clipboard__value');

    if (!this.copyButton || !this.valueElement) {
      return;
    }

    this.copyButton.addEventListener('click', this.handleCopy);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.copyButton) {
      this.copyButton.removeEventListener('click', this.handleCopy);
    }

    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  }

  /**
   * Handles copy button click
   * Copies text to clipboard and shows success state
   * @param {Event} event - Click event
   */
  async handleCopy(event) {
    if (this.copyButton.classList.contains('copy-to-clipboard--copied')) return;

    event.preventDefault();

    const copyValue = this.valueElement.textContent.trim();

    if (!copyValue) {
      return;
    }

    try {
      await this.copyToClipboard(copyValue);

      this.copyButton.classList.add('copy-to-clipboard--copied');

      this.dispatchEvent(new CustomEvent('copy:success', {
        bubbles: true,
        detail: { value: copyValue }
      }));

      const timeout = this.dataset.timeout || 2000;
      this.resetTimeoutId = setTimeout(() => {
        this.copyButton.classList.remove('copy-to-clipboard--copied');
        this.resetTimeoutId = null;
      }, parseInt(timeout));

    } catch (error) {
      this.dispatchEvent(new CustomEvent('copy:error', {
        bubbles: true,
        detail: { error }
      }));

      this.copyButton.classList.remove('copy-to-clipboard--copied');
    }
  }

  /**
   * Copies text to clipboard using Clipboard API with fallback
   * @param {string} text - Text to copy
   */
  async copyToClipboard(text) {
    // Try modern Clipboard API first
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (error) {
        console.warn('Clipboard API blocked, using fallback method:', error);
      }
    }

    this.#copyToClipboardFallback(text);
  }

  /**
   * Fallback method using execCommand
   * @param {string} text - Text to copy
   */
  #copyToClipboardFallback(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (!successful) {
        throw new Error('execCommand copy failed');
      }
    } finally {
      document.body.removeChild(textArea);
    }
  }

}

if (!customElements.get('copy-to-clipboard')) {
  customElements.define('copy-to-clipboard', CopyToClipboard);
}